// Module 7 — Workbench Engagement pane.
//
// Attaches / detaches an engagement to the scenario and exposes a tabbed
// editor (Warhead / Fuzing / Pk curve / Salvo). Live monotonicity
// validation on the Pk curve editor blocks saves with an inline error.
// Salvo tab renders a timeline preview of shot offsets.
//
// Layout mirrors WorkbenchInterceptorPane for consistency.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Plus, Trash2, X, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SpinnerButton from "@/components/SpinnerButton";
import EmptyState from "@/components/EmptyState";
import { sim } from "@/api/endpoints";
import {
    LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip,
    CartesianGrid, ReferenceDot,
} from "recharts";

const WARHEAD_OPTIONS = [
    { id: "blast_fragmentation", label: "Blast-fragmentation" },
    { id: "kinetic_impact", label: "Kinetic impact" },
    { id: "net_capture", label: "Net capture" },
    { id: "directed_energy", label: "Directed energy" },
];
const FUZE_OPTIONS = [
    { id: "proximity", label: "Proximity" },
    { id: "contact", label: "Contact" },
    { id: "time_after_launch", label: "Time after launch" },
];
const SALVO_OPTIONS = [
    { id: "single", label: "Single shot" },
    { id: "salvo", label: "Salvo (N shots)" },
    { id: "shoot_look_shoot", label: "Shoot-Look-Shoot" },
    { id: "shoot_shoot_look", label: "Shoot-Shoot-Look" },
];
const TABS = [
    { id: "warhead", label: "Warhead" },
    { id: "fuzing", label: "Fuzing" },
    { id: "pk", label: "Pk curve" },
    { id: "salvo", label: "Salvo" },
];

// Phase 18 close-off Module 7 — paramValue + checkPkMonotonic
// extracted to ./engagementHelpers so they can be unit-tested without
// dragging in the pane's API client and route deps. The pane re-exports
// them so existing internal call-sites stay one-import away.
import { paramValue, checkPkMonotonic } from "./engagementHelpers";
export { paramValue, checkPkMonotonic };

// ---------------------------------------------------------------------
// Attach selector — rendered when the scenario has no engagement yet.
// ---------------------------------------------------------------------
function AttachSelector({ scenario, onAttached }) {
    const [opts, setOpts] = useState([]);
    const [sel, setSel] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const list = await sim.engagements.list({ scope: "tenant" });
                setOpts(Array.isArray(list) ? list : []);
            } catch (e) {
                toast.error(formatApiError(e, "Load failed"));
            }
        })();
    }, []);

    const attach = async () => {
        if (!sel) return;
        setBusy(true);
        try {
            await sim.scenarios.attachEngagement(scenario.id, sel);
            toast.success("Engagement attached");
            onAttached?.();
        } catch (e) {
            toast.error(formatApiError(e, "Attach failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="workbench-engagement-empty">
            <EmptyState
                title="No engagement attached"
                body="Attach an engagement (preset or one you own) to compute fuze timing, Pk samples, and salvo outcomes."
            />
            <div className="flex items-center gap-2">
                <Select value={sel} onValueChange={setSel}>
                    <SelectTrigger
                        className="w-[340px] bg-ux-bg-2 text-[12px] font-mono"
                        data-testid="workbench-engagement-pick"
                    >
                        <SelectValue placeholder="Pick an engagement…" />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {opts.map((o) => (
                            <SelectItem
                                key={o.id}
                                value={o.id}
                                className="text-[12px] font-mono"
                            >
                                {o.name}{o.is_preset ? " (preset)" : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <SpinnerButton
                    size="sm"
                    disabled={!sel}
                    loading={busy}
                    onClick={attach}
                    testId="workbench-engagement-attach"
                >
                    Attach
                </SpinnerButton>
                <Link
                    to="/plan/engagements"
                    className="inline-flex items-center gap-2 text-[12px] text-ux-accent hover:underline ml-auto"
                    data-testid="workbench-engagement-open-lib"
                >
                    Open library <ExternalLink className="w-3.5 h-3.5" />
                </Link>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Pk curve editor — editable table + recharts preview
// ---------------------------------------------------------------------
function PkCurveEditor({ engagement, readOnly, onSaved }) {
    const initial = engagement?.pk_curve || {};
    const [rows, setRows] = useState(
        (initial.miss_distance_m || []).map((m, i) => ({
            m: Number(m),
            pk: Number((initial.pk || [])[i] ?? 0),
        })),
    );
    const [busy, setBusy] = useState(false);

    const monoError = useMemo(() => {
        return checkPkMonotonic(rows.map((r) => r.m), rows.map((r) => r.pk));
    }, [rows]);

    const updateRow = (i, key, val) => {
        const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: Number(val) } : r));
        setRows(next);
    };
    const addRow = () => {
        const last = rows[rows.length - 1] || { m: 0, pk: 1 };
        setRows([...rows, { m: last.m + 1, pk: Math.max(0, last.pk - 0.1) }]);
    };
    const removeRow = (i) => {
        if (rows.length <= 2) return;
        setRows(rows.filter((_, idx) => idx !== i));
    };

    const save = async () => {
        if (monoError) {
            toast.error("Pk curve must be monotonic non-increasing.");
            return;
        }
        setBusy(true);
        try {
            await sim.engagements.patch(engagement.id, {
                pk_curve: {
                    ...initial,
                    miss_distance_m: rows.map((r) => r.m),
                    pk: rows.map((r) => r.pk),
                    source: "user",
                },
            });
            toast.success("Pk curve saved");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    const chartData = rows.map((r) => ({ miss_m: r.m, pk: r.pk }));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="pk-curve-editor">
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[12px] text-ux-text-2 font-mono uppercase tracking-wider">
                        Points ({rows.length})
                    </div>
                    {!readOnly && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={addRow}
                            data-testid="pk-curve-add"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add point
                        </Button>
                    )}
                </div>
                <table className="w-full text-[12px] font-mono" data-testid="pk-curve-table">
                    <thead>
                        <tr className="text-left text-ux-text-3 border-b border-ux-border-1">
                            <th className="py-1.5">miss (m)</th>
                            <th className="py-1.5">pk</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-ux-border-1/40">
                                <td className="py-1">
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={r.m}
                                        readOnly={readOnly}
                                        className="h-7 bg-ux-bg-2 text-[12px] tabular-nums"
                                        onChange={(e) => updateRow(i, "m", e.target.value)}
                                        data-testid={`pk-curve-m-${i}`}
                                    />
                                </td>
                                <td className="py-1">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="1"
                                        value={r.pk}
                                        readOnly={readOnly}
                                        className="h-7 bg-ux-bg-2 text-[12px] tabular-nums"
                                        onChange={(e) => updateRow(i, "pk", e.target.value)}
                                        data-testid={`pk-curve-pk-${i}`}
                                    />
                                </td>
                                <td className="py-1 pl-2 text-right">
                                    {!readOnly && rows.length > 2 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRow(i)}
                                            className="text-ux-text-3 hover:text-ux-danger"
                                            data-testid={`pk-curve-remove-${i}`}
                                            aria-label="Remove row"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {monoError && (
                    <div
                        className="mt-2 text-[11px] text-ux-danger flex items-center gap-1"
                        role="alert"
                        data-testid="pk-curve-error"
                    >
                        <AlertTriangle className="w-3 h-3" /> {monoError}
                    </div>
                )}
                {!readOnly && (
                    <SpinnerButton
                        size="sm"
                        onClick={save}
                        loading={busy}
                        disabled={!!monoError}
                        className="mt-3"
                        testId="pk-curve-save"
                    >
                        Save curve
                    </SpinnerButton>
                )}
            </div>
            <div className="h-[240px]" data-testid="pk-curve-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 16 }}>
                        <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                        <XAxis
                            dataKey="miss_m"
                            type="number"
                            stroke="#64748b"
                            tick={{ fontSize: 11 }}
                            label={{ value: "miss (m)", position: "insideBottom", offset: -6, fill: "#94a3b8", fontSize: 11 }}
                        />
                        <YAxis
                            dataKey="pk"
                            type="number"
                            domain={[0, 1]}
                            stroke="#64748b"
                            tick={{ fontSize: 11 }}
                            label={{ value: "Pk", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
                        />
                        <RTooltip
                            contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 12 }}
                            labelFormatter={(v) => `${v} m`}
                            formatter={(v) => [v.toFixed(3), "Pk"]}
                        />
                        <Line
                            type="monotone"
                            dataKey="pk"
                            stroke="#14b8a6"
                            strokeWidth={2}
                            dot={{ r: 3, fill: "#14b8a6" }}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Salvo timeline preview
// ---------------------------------------------------------------------
function SalvoTimeline({ salvo }) {
    const mode = salvo?.mode || "single";
    const n = Math.max(1, Number(salvo?.shots_total || 1));
    const spacing = paramValue(salvo?.shots_spacing_s, 0) || 0;
    const bda = paramValue(salvo?.bda_window_s, 0) || 0;

    let events = [{ t: 0, label: "Shot 1" }];
    if (mode === "salvo") {
        for (let i = 1; i < n; i += 1) events.push({ t: i * spacing, label: `Shot ${i + 1}` });
    } else if (mode === "shoot_look_shoot") {
        events = [
            { t: 0, label: "Shot 1" },
            { t: bda || 3, label: "BDA" },
            { t: (bda || 3) + (spacing || 0.5), label: "Shot 2" },
        ];
    } else if (mode === "shoot_shoot_look") {
        events = [
            { t: 0, label: "Shot 1" },
            { t: spacing, label: "Shot 2" },
            { t: spacing + (bda || 3), label: "BDA" },
        ];
    }
    const maxT = Math.max(...events.map((e) => e.t), 1);

    return (
        <div className="space-y-2" data-testid="salvo-timeline">
            <div className="text-[12px] text-ux-text-2 font-mono uppercase tracking-wider">
                Timeline preview
            </div>
            <div className="relative h-14 border border-ux-border-1 rounded-md bg-ux-bg-2 px-4">
                <div className="absolute left-4 right-4 top-1/2 h-[2px] bg-ux-border-2" />
                {events.map((e, i) => {
                    const pct = (e.t / maxT) * 100;
                    const isBda = e.label === "BDA";
                    return (
                        <div
                            key={i}
                            className="absolute top-1/2 -translate-y-1/2"
                            style={{ left: `calc(1rem + ${pct}% - 1rem)` }}
                            data-testid={`salvo-event-${i}`}
                        >
                            <div
                                className={`w-3 h-3 rounded-full border-2 ${isBda ? "bg-amber-400 border-amber-400" : "bg-ux-accent border-ux-accent"}`}
                                title={`${e.label} @ t=${e.t.toFixed(2)}s`}
                            />
                            <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] font-mono text-ux-text-3 whitespace-nowrap">
                                {e.label}
                                <span className="ml-1 text-ux-text-3/70">{e.t.toFixed(2)}s</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Main pane
// ---------------------------------------------------------------------
export default function WorkbenchEngagementPane({ scenario, readOnly = false, onScenarioChanged }) {
    const [engagement, setEngagement] = useState(null);
    const [tab, setTab] = useState("warhead");
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        if (!scenario?.engagement_id) {
            setEngagement(null);
            return;
        }
        try {
            const fresh = await sim.engagements.get(scenario.engagement_id);
            setEngagement(fresh);
        } catch (e) {
            setEngagement(null);
            toast.error(formatApiError(e, "Load failed"));
        }
    }, [scenario?.engagement_id]);

    useEffect(() => {
        reload();
    }, [reload]);

    const effectiveReadOnly = readOnly || !!engagement?.is_preset;

    const detach = async () => {
        setBusy(true);
        try {
            await sim.scenarios.detachEngagement(scenario.id);
            toast.success("Engagement detached");
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Detach failed"));
        } finally {
            setBusy(false);
        }
    };

    const clonePreset = async () => {
        if (!engagement) return;
        setBusy(true);
        try {
            const cloned = await sim.engagements.clone(engagement.id);
            await sim.scenarios.attachEngagement(scenario.id, cloned.id);
            toast.success(`Cloned "${cloned.name}" and attached`);
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!scenario?.engagement_id || !engagement) {
        return (
            <AttachSelector
                scenario={scenario}
                onAttached={() => onScenarioChanged?.()}
            />
        );
    }

    return (
        <div className="space-y-5" data-testid="workbench-engagement-pane">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="text-[14px] font-medium text-ux-text-1">
                            {engagement.name}
                        </div>
                        {engagement.is_preset && (
                            <span className="text-[10px] font-mono uppercase tracking-wider border border-ux-border-1 text-ux-text-3 rounded-full px-2 py-0.5">
                                preset · read-only
                            </span>
                        )}
                    </div>
                    {engagement.notes && (
                        <p className="text-[12px] text-ux-text-3 mt-1">{engagement.notes}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {engagement.is_preset && !readOnly && (
                        <SpinnerButton
                            size="sm"
                            variant="outline"
                            loading={busy}
                            onClick={clonePreset}
                            testId="workbench-engagement-clone"
                        >
                            <Copy className="w-3.5 h-3.5 mr-1" /> Clone to edit
                        </SpinnerButton>
                    )}
                    {!readOnly && (
                        <SpinnerButton
                            size="sm"
                            variant="ghost"
                            loading={busy}
                            onClick={detach}
                            testId="workbench-engagement-detach"
                        >
                            <X className="w-3.5 h-3.5 mr-1" /> Detach
                        </SpinnerButton>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-ux-border-1" data-testid="engagement-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider transition-colors ${
                            tab === t.id
                                ? "text-ux-accent border-b-2 border-ux-accent -mb-px"
                                : "text-ux-text-3 hover:text-ux-text-1"
                        }`}
                        data-testid={`engagement-tab-${t.id}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div>
                {tab === "warhead" && <WarheadTab engagement={engagement} readOnly={effectiveReadOnly} onChanged={reload} />}
                {tab === "fuzing" && <FuzingTab engagement={engagement} readOnly={effectiveReadOnly} onChanged={reload} />}
                {tab === "pk" && <PkCurveEditor engagement={engagement} readOnly={effectiveReadOnly} onSaved={reload} />}
                {tab === "salvo" && <SalvoTab engagement={engagement} readOnly={effectiveReadOnly} onChanged={reload} />}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Warhead tab
// ---------------------------------------------------------------------
function WarheadTab({ engagement, readOnly, onChanged }) {
    const wh = engagement.warhead || {};
    const [type, setType] = useState(wh.type || "blast_fragmentation");
    const [lr, setLr] = useState(paramValue(wh.lethal_radius_m, 3));
    const [em, setEm] = useState(paramValue(wh.effective_mass_kg, 0.5));
    const [pattern, setPattern] = useState(wh.fragment_pattern || "isotropic");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await sim.engagements.patch(engagement.id, {
                warhead: {
                    type,
                    lethal_radius_m: { value: Number(lr), unit: "m", source: "user" },
                    effective_mass_kg: { value: Number(em), unit: "kg", source: "user" },
                    fragment_pattern: type === "blast_fragmentation" ? pattern : null,
                },
            });
            toast.success("Warhead saved");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="warhead-tab">
            <LabeledSelect
                label="Warhead type"
                value={type}
                onChange={setType}
                options={WARHEAD_OPTIONS}
                disabled={readOnly}
                testId="warhead-type"
            />
            <LabeledNumber
                label="Lethal radius (m)"
                value={lr}
                onChange={setLr}
                readOnly={readOnly}
                testId="warhead-lethal-radius"
            />
            <LabeledNumber
                label="Effective mass (kg)"
                value={em}
                onChange={setEm}
                readOnly={readOnly}
                testId="warhead-effective-mass"
            />
            {type === "blast_fragmentation" && (
                <LabeledSelect
                    label="Fragment pattern"
                    value={pattern}
                    onChange={setPattern}
                    options={[
                        { id: "isotropic", label: "Isotropic" },
                        { id: "forward_cone", label: "Forward cone" },
                    ]}
                    disabled={readOnly}
                    testId="warhead-fragment-pattern"
                />
            )}
            {!readOnly && (
                <div className="md:col-span-2">
                    <SpinnerButton size="sm" onClick={save} loading={busy} testId="warhead-save">
                        Save warhead
                    </SpinnerButton>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Fuzing tab
// ---------------------------------------------------------------------
function FuzingTab({ engagement, readOnly, onChanged }) {
    const [mode, setMode] = useState(engagement.fuze_mode || "proximity");
    const [prox, setProx] = useState(engagement.proximity || {});
    const [ctc, setCtc] = useState(engagement.contact || {});
    const [tim, setTim] = useState(engagement.time_after_launch || {});
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const body = {
                fuze_mode: mode,
                proximity: mode === "proximity" ? {
                    trigger_distance_m: { value: Number(paramValue(prox.trigger_distance_m, 2)), unit: "m", source: "user" },
                    arming_distance_m: { value: Number(paramValue(prox.arming_distance_m, 5)), unit: "m", source: "user" },
                    arming_time_s: { value: Number(paramValue(prox.arming_time_s, 0.2)), unit: "s", source: "user" },
                } : null,
                contact: mode === "contact" ? {
                    arming_time_s: { value: Number(paramValue(ctc.arming_time_s, 0.3)), unit: "s", source: "user" },
                } : null,
                time_after_launch: mode === "time_after_launch" ? {
                    trigger_time_s: { value: Number(paramValue(tim.trigger_time_s, 1.5)), unit: "s", source: "user" },
                    arming_time_s: { value: Number(paramValue(tim.arming_time_s, 0.3)), unit: "s", source: "user" },
                } : null,
            };
            await sim.engagements.patch(engagement.id, body);
            toast.success("Fuzing saved");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="fuzing-tab">
            <LabeledSelect
                label="Fuze mode"
                value={mode}
                onChange={setMode}
                options={FUZE_OPTIONS}
                disabled={readOnly}
                testId="fuze-mode"
            />
            {mode === "proximity" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <LabeledNumber label="Trigger distance (m)" value={paramValue(prox.trigger_distance_m, 2)} onChange={(v) => setProx({ ...prox, trigger_distance_m: { value: v, unit: "m", source: "user" } })} readOnly={readOnly} testId="fuze-trigger-distance" />
                    <LabeledNumber label="Arming distance (m)" value={paramValue(prox.arming_distance_m, 5)} onChange={(v) => setProx({ ...prox, arming_distance_m: { value: v, unit: "m", source: "user" } })} readOnly={readOnly} testId="fuze-arming-distance" />
                    <LabeledNumber label="Arming time (s)" value={paramValue(prox.arming_time_s, 0.2)} onChange={(v) => setProx({ ...prox, arming_time_s: { value: v, unit: "s", source: "user" } })} readOnly={readOnly} testId="fuze-arming-time" />
                </div>
            )}
            {mode === "contact" && (
                <LabeledNumber label="Arming time (s)" value={paramValue(ctc.arming_time_s, 0.3)} onChange={(v) => setCtc({ ...ctc, arming_time_s: { value: v, unit: "s", source: "user" } })} readOnly={readOnly} testId="fuze-contact-arming-time" />
            )}
            {mode === "time_after_launch" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <LabeledNumber label="Trigger time (s)" value={paramValue(tim.trigger_time_s, 1.5)} onChange={(v) => setTim({ ...tim, trigger_time_s: { value: v, unit: "s", source: "user" } })} readOnly={readOnly} testId="fuze-trigger-time" />
                    <LabeledNumber label="Arming time (s)" value={paramValue(tim.arming_time_s, 0.3)} onChange={(v) => setTim({ ...tim, arming_time_s: { value: v, unit: "s", source: "user" } })} readOnly={readOnly} testId="fuze-time-arming-time" />
                </div>
            )}
            {!readOnly && (
                <SpinnerButton size="sm" onClick={save} loading={busy} testId="fuzing-save">
                    Save fuzing
                </SpinnerButton>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Salvo tab — mode selector + timeline preview
// ---------------------------------------------------------------------
function SalvoTab({ engagement, readOnly, onChanged }) {
    const s = engagement.salvo || {};
    const [mode, setMode] = useState(s.mode || "single");
    const [n, setN] = useState(s.shots_total || 1);
    const [spacing, setSpacing] = useState(paramValue(s.shots_spacing_s, 1.0) ?? 1.0);
    const [bda, setBda] = useState(paramValue(s.bda_window_s, 3.0) ?? 3.0);
    const [reassign, setReassign] = useState(!!s.reassign_if_miss);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const body = {
                salvo: {
                    mode,
                    shots_total: Math.max(1, Number(n)),
                    shots_spacing_s: mode === "single" ? null : { value: Number(spacing), unit: "s", source: "user" },
                    bda_window_s: (mode === "shoot_look_shoot" || mode === "shoot_shoot_look") ? { value: Number(bda), unit: "s", source: "user" } : null,
                    reassign_if_miss: reassign,
                },
            };
            await sim.engagements.patch(engagement.id, body);
            toast.success("Salvo saved");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    // Compose the timeline model for preview.
    const preview = {
        mode,
        shots_total: n,
        shots_spacing_s: { value: Number(spacing) },
        bda_window_s: { value: Number(bda) },
    };

    return (
        <div className="space-y-5" data-testid="salvo-tab">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LabeledSelect
                    label="Salvo mode"
                    value={mode}
                    onChange={setMode}
                    options={SALVO_OPTIONS}
                    disabled={readOnly}
                    testId="salvo-mode"
                />
                <LabeledNumber
                    label="Shots total"
                    value={n}
                    onChange={setN}
                    readOnly={readOnly}
                    testId="salvo-shots-total"
                />
                {mode !== "single" && (
                    <LabeledNumber
                        label="Shot spacing (s)"
                        value={spacing}
                        onChange={setSpacing}
                        readOnly={readOnly}
                        testId="salvo-spacing"
                    />
                )}
                {(mode === "shoot_look_shoot" || mode === "shoot_shoot_look") && (
                    <LabeledNumber
                        label="BDA window (s)"
                        value={bda}
                        onChange={setBda}
                        readOnly={readOnly}
                        testId="salvo-bda-window"
                    />
                )}
                {mode !== "single" && (
                    <label className="flex items-center gap-2 text-[12px] text-ux-text-2 font-mono">
                        <input
                            type="checkbox"
                            checked={reassign}
                            onChange={(e) => setReassign(e.target.checked)}
                            disabled={readOnly}
                            data-testid="salvo-reassign"
                        />
                        Reassign if miss
                    </label>
                )}
            </div>
            <SalvoTimeline salvo={preview} />
            {!readOnly && (
                <SpinnerButton size="sm" onClick={save} loading={busy} testId="salvo-save">
                    Save salvo
                </SpinnerButton>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Small field helpers
// ---------------------------------------------------------------------
function LabeledSelect({ label, value, onChange, options, disabled, testId }) {
    return (
        <label className="flex flex-col gap-1.5 text-[12px] text-ux-text-2">
            <span className="font-mono uppercase tracking-wider text-[11px] text-ux-text-3">{label}</span>
            <Select value={value} onValueChange={onChange} disabled={disabled}>
                <SelectTrigger
                    className="h-8 bg-ux-bg-2 text-[12px] font-mono"
                    data-testid={testId}
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                    {options.map((o) => (
                        <SelectItem key={o.id} value={o.id} className="text-[12px] font-mono">
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </label>
    );
}

function LabeledNumber({ label, value, onChange, readOnly, testId }) {
    return (
        <label className="flex flex-col gap-1.5 text-[12px] text-ux-text-2">
            <span className="font-mono uppercase tracking-wider text-[11px] text-ux-text-3">{label}</span>
            <Input
                type="number"
                step="any"
                value={value ?? ""}
                readOnly={readOnly}
                onChange={(e) => onChange?.(e.target.value === "" ? 0 : Number(e.target.value))}
                className="h-8 bg-ux-bg-2 text-[12px] font-mono tabular-nums"
                data-testid={testId}
            />
        </label>
    );
}
